import React from 'react';
import { Check } from 'lucide-react';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { FristZeichen, NachweisStatusZeichen, RisikoZeichen } from './StatusBadge';
import { HANDBUCH_FARBEN } from './stil';
import { formatDatum } from '../../../services/assignmentLogic';
import {
    anhangIIPlanungHinweise, anhangIIVorschlagOffen, einrichtungenDesGewerks, fristLage,
    koordinierungsSchluessel, psaVorschlag, schritteDesGewerks, wirksamerZeitraumDerFassung,
} from '../handbuchLogic';
import { anhangIINummerText, sortiereAnhangII } from '../anhangII';
import { alsTag, tageEinschliesslich } from '../../koordinierung/ueberschneidung';
import { paarKey } from '../../koordinierung/koordinierungLogic';
import type { KoordinierungPaar } from '../../koordinierung/koordinierungLogic';
import type {
    Arbeitsschritt, Gefaehrdung, NotrufplanInhalt, Schutzmassnahme,
} from '../../sige-plan/types';
import type { ProjektDokument } from '../../dokumente/types';
import type {
    GewerkAusPlan, Handbuch, HandbuchGewerk, HandbuchNachweis, PsaEintrag, RisikoStufe,
} from '../types';

/**
 * Kapitel „Gewerkeseite" — ein A3-Blatt je Gewerk.
 *
 * Layout portiert aus `pages/editor/GewerkPage.tsx` der Quell-App (Kopfleiste
 * mit Stammdaten, 2×2-Raster, Gantt-Leiste über die volle Breite). Zwei
 * Unterschiede, beide mit Grund:
 *
 * 1. **Ein Blatt statt zwei.** Die Quell-App druckt je Gewerk zwei Seiten. Der
 *    Seitenspiegel dieses Handbuchs (`seitenPlan` in `handbuchLogic.ts`) zählt
 *    eine Seite je Gewerk; zwei Blätter hier und eine Zeile dort ergäben ein
 *    „Seite 7 von 11" auf einem Dokument mit 15 Blättern. Die Fußzeile ist der
 *    Vollständigkeitsnachweis des Handbuchs — sie darf nicht raten.
 * 2. **Die Felder sind neu gebunden.** Die Quell-App hält alles am
 *    `Gewerk`-Objekt ihres Stores. Hier kommt jeder Block aus seiner eigenen
 *    Quelle: Tätigkeiten und Zuständigkeiten aus `sige_arbeitsschritte`,
 *    Gefährdungen aus `sige_gefaehrdungen`, Maßnahmen aus
 *    `sige_schutzmassnahmen`, Schnittstellen aus `koordinierung_paare` — und
 *    nur das, was es sonst nirgends gibt, aus `handbuch_gewerke`.
 *
 * ═══ VORBELEGUNG IST EIN VORSCHLAG, KEINE TATSACHE ═════════════════════════
 * Anhang II wird aus `vorankuendigung.selectedDangerousWorks` vorgeschlagen,
 * die PSA aus den Bausteinen (`hinweis_codes`) und der Gewerkvorlage. Beides
 * erscheint deutlich getrennt vom Bestand und wandert erst auf Knopfdruck in
 * die Gewerkeseite. Ein ungefragt gesetztes Anhang-II-Merkmal wäre eine
 * Feststellung des SiGeKo, die niemand getroffen hat — und die Vorankündigung
 * meint das Projekt, nicht dieses eine Gewerk.
 *
 * ═══ LESENDE QUELLEN BLEIBEN LESEND ════════════════════════════════════════
 * Geschrieben wird ausschließlich in `handbuch_gewerke` und
 * `handbuch_nachweise`, und zwar über die Rückrufe. In `sige_*`,
 * `report.vorankuendigung`, `report.unterlage` und `metadata.*` fasst diese
 * Seite nichts an.
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// Was eine fachliche Regel trägt, liegt in `handbuchLogic.ts` und ist dort
// geprüft: `wirksamerZeitraumDerFassung`, `schritteDesGewerks`, `psaVorschlag`,
// `anhangIIVorschlagOffen`, `einrichtungenDesGewerks`, `bausteineDesSchritts`.
// Hier bleibt, was allein dieses Blatt zeichnet — Balkenanteile, Rangfolge,
// Zuständigkeitsspalten. Nicht exportiert:
// `react-refresh/only-export-components` lässt in einer Komponentendatei keine
// Wertexporte zu, und `npm run lint` läuft mit `--max-warnings 0`.
// ═══════════════════════════════════════════════════════════════════════════

/** Schluessel eines PSA-Eintrags — hier nur als React-`key`. Die fachliche
 *  Entdopplung macht `psaVorschlag` in `handbuchLogic.ts`. */
const psaSchluessel = (p: PsaEintrag): string =>
    `${(p.bezeichnung || '').trim().toLowerCase()}|${(p.norm || '').trim().toLowerCase()}`;

interface Zustaendigkeit {
    rolle: string;
    personen: string[];
}

/** Zuständigkeiten aus den Arbeitsschritten. Drei feste Rollen — der SiGe-Plan
 *  kennt genau diese drei Spalten, eine vierte zu erfinden hieße, sie zu
 *  behaupten. */
const zustaendigkeiten = (schritte: Arbeitsschritt[]): Zustaendigkeit[] => {
    const sammle = (feld: keyof Arbeitsschritt): string[] => {
        const raus: string[] = [];
        schritte.forEach(s => {
            const wert = String(s[feld] ?? '').trim();
            if (wert && !raus.includes(wert)) raus.push(wert);
        });
        return raus;
    };
    return [
        { rolle: 'Erstellung', personen: sammle('zustaendig_erstellung') },
        { rolle: 'Kontrolle', personen: sammle('zustaendig_kontrolle') },
        { rolle: 'Instandhaltung', personen: sammle('zustaendig_instandhaltung') },
    ].filter(z => z.personen.length > 0);
};

/**
 * Lage eines Datums auf der Projektachse, 0..1.
 *
 * Ohne `Date`: `tageEinschliesslich` rechnet über eine Kalenderformel, der
 * String-Konstruktor legt „2026-01-01" dagegen als Mitternacht UTC aus und
 * verschiebt in negativen Zeitzonen um einen Tag. Genau dieser Fehler steckt
 * in `dateToDays` der Quell-App (`GewerkPage.tsx:18-20`) und wird hier nicht
 * mitportiert.
 */
const anteil = (tag: string, start: string, spanneTage: number): number => {
    const roh = (tageEinschliesslich(start, tag) - 1) / spanneTage;
    return roh < 0 ? 0 : roh > 1 ? 1 : roh;
};

/** Das Ende schließt seinen letzten Tag MIT ein — sonst endete ein eintägiger
 *  Einsatz auf einem Strich der Breite null. */
const anteilEnde = (tag: string, start: string, spanneTage: number): number => {
    const roh = tageEinschliesslich(start, tag) / spanneTage;
    return roh < 0 ? 0 : roh > 1 ? 1 : roh;
};

const prozent = (wert: number): string => `${(wert * 100).toFixed(3)}%`;

/** Die Gefährdungen eines Gewerks, schwerste zuerst. */
const RANG: Record<RisikoStufe, number> = { hoch: 0, mittel: 1, niedrig: 2 };

// ═══════════════════════════════════════════════════════════════════════════
// DARSTELLUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Der Knopf, mit dem ein Vorschlag zur Tatsache wird. Nur am Bildschirm —
 *  auf dem gedruckten Blatt wäre er ein Fleck. */
const UebernehmenKnopf: React.FC<{ text: string; onClick: () => void }> = ({ text, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="print:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-400 bg-white text-[9px] font-bold text-slate-700 hover:bg-slate-100"
    >
        <Check size={10} />
        {text}
    </button>
);

const Kopfleiste: React.FC<{
    gewerk: HandbuchGewerk;
    zeitraum: { von: string | null; bis: string | null };
}> = ({ gewerk, zeitraum }) => (
    <div
        className="flex flex-wrap gap-x-5 shrink-0"
        style={{
            border: `1px solid ${HANDBUCH_FARBEN.linie}`,
            background: HANDBUCH_FARBEN.abschnittHintergrund,
            padding: '3px 8px',
            marginBottom: '6px',
            fontSize: '9px',
        }}
    >
        <span><strong>GEWERK:</strong> {gewerk.gewerk_label}</span>
        {gewerk.din_nummer && <span>{gewerk.din_nummer}</span>}
        {/* Leere Felder werden benannt und nicht weggelassen: „Firma: —" ist
            eine Fehlstelle, ein fehlendes Wort ist unsichtbar. */}
        <span><strong>Firma:</strong> {gewerk.firma || '—'}</span>
        <span><strong>Verantw.:</strong> {gewerk.verantwortlich || '—'}</span>
        <span><strong>Tel.:</strong> {gewerk.telefon || '—'}</span>
        <span><strong>Beschäftigte:</strong> {gewerk.anzahl_beschaeftigte ?? '—'}</span>
        <span>
            <strong>Zeitraum:</strong>{' '}
            {zeitraum.von && zeitraum.bis
                ? `${formatDatum(zeitraum.von)} – ${formatDatum(zeitraum.bis)}`
                : 'nicht hinterlegt'}
        </span>
    </div>
);

interface Props {
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Die Gewerkeseite, um die es geht. */
    gewerk: HandbuchGewerk;
    /**
     * Der Handbuchsatz — er entscheidet, OB der Zeitraum noch aus dem Plan
     * gerechnet werden darf.
     *
     * Eine freigegebene Fassung greift nicht mehr auf den laufenden Plan
     * zurueck: sonst zeigte derselbe Druck derselben Fassung nach einer
     * Terminverschiebung andere Daten, ohne Historieneintrag. Der Entwurf
     * liest weiter live, denn dort SOLL die Verschiebung auffallen. Die
     * Unterscheidung trifft `wirksamerZeitraumDerFassung`.
     *
     * Getrennt von `sperrHinweisText` und `darfAendern`: die beiden sagen,
     * ob geschrieben werden darf, dieser Satz sagt, WAS gelesen wird.
     */
    handbuch?: Handbuch | null;
    /** Alle Gewerkeseiten des Handbuchs — für die Gantt-Leiste der Nachbarn. */
    alleGewerke: HandbuchGewerk[];
    /** Die Nachweise DIESER Gewerkeseite. */
    nachweise: HandbuchNachweis[];
    /** Projektablage, um den Dateinamen eines belegten Nachweises zu zeigen. */
    dokumente?: ProjektDokument[];
    /** Gewerke der Planfassung — liefern Zeitraum und Katalogzuordnung. */
    planGewerke?: GewerkAusPlan[];
    /** Alle Arbeitsschritte der Planfassung. */
    schritte?: Arbeitsschritt[];
    gefaehrdungen?: Gefaehrdung[];
    massnahmen?: Schutzmassnahme[];
    /** Inhalt des Blocks `sige_bloecke[typ='notrufplan']`. Wird NUR gelesen —
     *  es gibt genau einen Notrufplan je Projekt, und der gehört dem SiGe-Plan. */
    notrufplan?: NotrufplanInhalt | null;
    /** Bewertungen aus `koordinierung_paare`. */
    paare?: KoordinierungPaar[];
    /** `vorankuendigung.selectedDangerousWorks` — projektweiter Vorschlag. */
    anhangIIVorschlag?: string[];
    /**
     * Zutreffende Anhang-II-Nummern laut Planungsphase (Projektsteckbrief
     * `anhang2.nummern.wert`). Ergibt nur HINWEISE am Blatt, nie ein Kreuz:
     * die Planungsphase meint das Projekt, nicht dieses Gewerk
     * (`anhangIIPlanungHinweise`). Fehlt die Prop, erscheint nichts.
     */
    anhangIIPlanung?: readonly number[] | null;
    /** Heutiges ISO-Tagesdatum für die Fristrechnung. */
    heuteIso: string;
    projektVon?: string | null;
    projektBis?: string | null;
    /** `false` sperrt jeden Übernahmeknopf. Der Aufrufer bildet das aus
     *  `isReadOnly` UND `istGesperrt(handbuch)` — zwei Reihen Schreibschutz. */
    darfAendern?: boolean;
    /** Satz zur Freigabesperre (`sperrHinweis`), falls eine besteht. */
    sperrHinweisText?: string | null;
    onAnhangIIUebernehmen?: (werte: string[]) => void;
    onPsaUebernehmen?: (eintraege: PsaEintrag[]) => void;
}

export const GewerkSeite: React.FC<Props> = ({
    kopf, fuss, gewerk, handbuch = null, alleGewerke, nachweise, dokumente = [], planGewerke = [],
    schritte = [], gefaehrdungen = [], massnahmen = [], notrufplan = null, paare = [],
    anhangIIVorschlag = [], anhangIIPlanung = null, heuteIso, projektVon, projektBis,
    darfAendern = false, sperrHinweisText, onAnhangIIUebernehmen, onPsaUebernehmen,
}) => {
    const ausPlan = new Map<string, GewerkAusPlan>((planGewerke || []).map(g => [g.gewerk_norm, g]));
    const zeitraum = wirksamerZeitraumDerFassung(gewerk, ausPlan, handbuch);
    const katalogId = ausPlan.get(gewerk.gewerk_norm)?.katalogId ?? null;

    const meineSchritte = schritteDesGewerks(gewerk, schritte);
    const schrittIds = new Set(meineSchritte.map(s => s.id));
    const meineGefaehrdungen = (gefaehrdungen || []).filter(g => schrittIds.has(g.arbeitsschritt_id));
    const gefahrIds = new Set(meineGefaehrdungen.map(g => g.id));
    const meineMassnahmen = (massnahmen || []).filter(m => gefahrIds.has(m.gefaehrdung_id));

    const massnahmenNachGefahr = new Map<string, Schutzmassnahme[]>();
    meineMassnahmen.forEach(m => {
        const liste = massnahmenNachGefahr.get(m.gefaehrdung_id);
        if (liste) liste.push(m);
        else massnahmenNachGefahr.set(m.gefaehrdung_id, [m]);
    });

    const sortierteGefaehrdungen = [...meineGefaehrdungen].sort(
        (a, b) => (RANG[a.prioritaet] ?? 1) - (RANG[b.prioritaet] ?? 1),
    );

    const offenerPsaVorschlag = psaVorschlag(gewerk, meineSchritte, katalogId);
    const offenerAnhangII = anhangIIVorschlagOffen(gewerk, anhangIIVorschlag);
    const planungHinweise = anhangIIPlanungHinweise(gewerk, anhangIIPlanung);
    const meineEinrichtungen = einrichtungenDesGewerks(meineSchritte, schritte);
    const meineZustaendigkeiten = zustaendigkeiten(meineSchritte);

    const dokumentNach = new Map<string, ProjektDokument>((dokumente || []).map(d => [d.id, d]));
    const sortierteNachweise = [...(nachweise || [])].sort((a, b) => a.sortierung - b.sortierung);

    // ─── Gantt-Leiste: eigene Bauzeit gegen die der Nachbarn ───────────────
    const nachbarn = (alleGewerke || [])
        .filter(g => g.id !== gewerk.id)
        .sort((a, b) => a.sortierung - b.sortierung);

    const alleZeitraeume = [gewerk, ...nachbarn].map(g => wirksamerZeitraumDerFassung(g, ausPlan, handbuch));
    const achsenStart = [alsTag(projektVon), ...alleZeitraeume.map(z => alsTag(z.von))]
        .filter((t): t is string => !!t)
        .sort()[0] ?? null;
    const achsenEnde = [alsTag(projektBis), ...alleZeitraeume.map(z => alsTag(z.bis))]
        .filter((t): t is string => !!t)
        .sort()
        .reverse()[0] ?? null;
    const spanneTage = achsenStart && achsenEnde && achsenStart <= achsenEnde
        ? tageEinschliesslich(achsenStart, achsenEnde)
        : 0;

    const meinSchluessel = koordinierungsSchluessel(gewerk);
    const paareNach = new Map<string, KoordinierungPaar[]>();
    (paare || []).forEach(z => {
        const key = paarKey(z.gewerk_a_norm || '', z.gewerk_b_norm || '');
        const liste = paareNach.get(key);
        if (liste) liste.push(z);
        else paareNach.set(key, [z]);
    });

    const balken = (von: string | null, bis: string | null, hoch: boolean): React.ReactNode => {
        const a = alsTag(von);
        const b = alsTag(bis);
        if (!a || !b || !achsenStart || spanneTage <= 0 || a > b) {
            return <span style={{ fontSize: '7px', color: HANDBUCH_FARBEN.textLeise }}>kein Zeitraum</span>;
        }
        const links = anteil(a, achsenStart, spanneTage);
        const rechts = anteilEnde(b, achsenStart, spanneTage);
        return (
            <div className="relative" style={{ height: '9px', background: '#f4f4f4' }}>
                <div
                    className="absolute"
                    style={{
                        left: prozent(links),
                        width: prozent(rechts - links),
                        top: hoch ? '1px' : '2px',
                        height: hoch ? '7px' : '5px',
                        minWidth: '2px',
                        background: hoch ? HANDBUCH_FARBEN.linieStark : '#8a8a8a',
                        borderRadius: '1px',
                    }}
                />
            </div>
        );
    };

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <Kopfleiste gewerk={gewerk} zeitraum={zeitraum} />

            {sperrHinweisText && (
                <div
                    className="print:hidden shrink-0"
                    style={{
                        border: `1px solid ${HANDBUCH_FARBEN.risikoMittel}`,
                        background: '#fdf6e8',
                        color: HANDBUCH_FARBEN.risikoMittel,
                        padding: '2px 6px',
                        fontSize: '8px',
                        marginBottom: '5px',
                    }}
                >
                    {sperrHinweisText}
                </div>
            )}

            <div className="grid grid-cols-2 grid-rows-2 gap-[6px] flex-1 min-h-0">
                {/* ─── oben links: Tätigkeiten ────────────────────────────── */}
                <Abschnitt titel="Tätigkeiten / Arbeitsabläufe" zusatz={`${meineSchritte.length}`}>
                    {meineSchritte.length === 0 ? (
                        <AbschnittLeer text="Im SiGe-Plan ist diesem Gewerk kein Arbeitsschritt zugeordnet." />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Nr', 'Tätigkeit', 'Bereich', 'Hauptabschnitt']}
                            breiten={['7%', '43%', '22%', '28%']}
                        >
                            {meineSchritte.map((s, i) => (
                                <AbschnittZeile key={s.id} erste={i === 0}>
                                    <AbschnittZelle>{i + 1}</AbschnittZelle>
                                    <AbschnittZelle>{s.bezeichnung}</AbschnittZelle>
                                    <AbschnittZelle>{s.raeumliche_zuordnung || '—'}</AbschnittZelle>
                                    <AbschnittZelle>{s.hauptabschnitt || '—'}</AbschnittZelle>
                                </AbschnittZeile>
                            ))}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>

                {/* ─── oben rechts: Eigengefährdungen und Schutzmaßnahmen ── */}
                <Abschnitt
                    titel="Eigengefährdungen und Schutzmaßnahmen"
                    zusatz={`${sortierteGefaehrdungen.length}`}
                >
                    {sortierteGefaehrdungen.length === 0 ? (
                        <AbschnittLeer text="Im SiGe-Plan ist zu den Arbeitsschritten dieses Gewerks keine Gefährdung erfasst." />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Gefährdung', 'Risiko', 'Schutzmaßnahme', 'Verantwortlich', 'Regelwerk']}
                            breiten={['28%', '10%', '32%', '15%', '15%']}
                        >
                            {sortierteGefaehrdungen.map((g, i) => {
                                const zug = massnahmenNachGefahr.get(g.id) || [];
                                return (
                                    <AbschnittZeile key={g.id} erste={i === 0}>
                                        <AbschnittZelle>
                                            {g.bezeichnung}
                                            {g.kategorie && (
                                                <div style={{ fontSize: '7px', color: HANDBUCH_FARBEN.textLeise }}>
                                                    {g.kategorie}
                                                </div>
                                            )}
                                        </AbschnittZelle>
                                        <AbschnittZelle>
                                            <RisikoZeichen risiko={g.prioritaet} />
                                        </AbschnittZelle>
                                        <AbschnittZelle>
                                            {zug.length === 0
                                                ? <span style={{ color: HANDBUCH_FARBEN.risikoHoch }}>keine erfasst</span>
                                                : zug.map(m => <div key={m.id}>{m.beschreibung}</div>)}
                                        </AbschnittZelle>
                                        <AbschnittZelle>
                                            {zug.map(m => <div key={m.id}>{m.verantwortlich || '—'}</div>)}
                                        </AbschnittZelle>
                                        <AbschnittZelle>
                                            {zug.map(m => <div key={m.id}>{m.regelwerk_referenz || '—'}</div>)}
                                        </AbschnittZelle>
                                    </AbschnittZeile>
                                );
                            })}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>

                {/* ─── unten links: PSA, Anhang II, gemeinsame Einrichtungen ─ */}
                <div className="flex flex-col gap-[6px] min-h-0">
                    <Abschnitt
                        titel="Persönliche Schutzausrüstung"
                        zusatz={darfAendern && offenerPsaVorschlag.length > 0 && onPsaUebernehmen ? (
                            <UebernehmenKnopf
                                text={`${offenerPsaVorschlag.length} Vorschlag/-schläge übernehmen`}
                                onClick={() => onPsaUebernehmen([...(gewerk.psa || []), ...offenerPsaVorschlag])}
                            />
                        ) : undefined}
                        className="flex-1"
                    >
                        {(gewerk.psa || []).length === 0 && offenerPsaVorschlag.length === 0 ? (
                            <AbschnittLeer />
                        ) : (
                            <AbschnittTabelle kopf={['PSA', 'Norm', 'Bei welcher Tätigkeit']} breiten={['38%', '22%', '40%']}>
                                {(gewerk.psa || []).map((p, i) => (
                                    <AbschnittZeile key={`psa-${psaSchluessel(p)}`} erste={i === 0}>
                                        <AbschnittZelle>{p.bezeichnung}</AbschnittZelle>
                                        <AbschnittZelle>{p.norm}</AbschnittZelle>
                                        <AbschnittZelle>{p.taetigkeiten}</AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                                {/* Vorschläge stehen sichtbar getrennt vom Bestand
                                    und tragen es im Klartext — ein Vorschlag, der
                                    aussieht wie eine Festlegung, IST eine
                                    Festlegung, sobald das Blatt gedruckt ist. */}
                                {offenerPsaVorschlag.map(p => (
                                    <AbschnittZeile key={`psa-vorschlag-${psaSchluessel(p)}`}>
                                        <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                                            {p.bezeichnung}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                                            {p.norm}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                                            Vorschlag aus Plan/Vorlage — noch nicht bestätigt
                                        </AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                            </AbschnittTabelle>
                        )}
                    </Abschnitt>

                    <Abschnitt
                        titel="Besonders gefährliche Arbeiten (Anhang II BaustellV)"
                        zusatz={darfAendern && offenerAnhangII.length > 0 && onAnhangIIUebernehmen ? (
                            <UebernehmenKnopf
                                text={`${offenerAnhangII.length} aus Vorankündigung übernehmen`}
                                onClick={() => onAnhangIIUebernehmen(
                                    sortiereAnhangII([...(gewerk.anhang_ii || []), ...offenerAnhangII]),
                                )}
                            />
                        ) : undefined}
                        className="shrink-0"
                    >
                        <div style={{ fontSize: '8.5px' }}>
                            {(gewerk.anhang_ii || []).length === 0 && offenerAnhangII.length === 0 ? (
                                <AbschnittLeer text="Keine Arbeit nach Anhang II festgestellt." />
                            ) : (
                                <>
                                    {sortiereAnhangII(gewerk.anhang_ii || []).map(a => (
                                        <div key={a}>
                                            ☑ {anhangIINummerText(a) ? `Nr. ${anhangIINummerText(a)} ` : ''}{a}
                                        </div>
                                    ))}
                                    {offenerAnhangII.map(a => (
                                        <div
                                            key={`vorschlag-${a}`}
                                            style={{ color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}
                                        >
                                            ☐ {anhangIINummerText(a) ? `Nr. ${anhangIINummerText(a)} ` : ''}{a}
                                            {' '}— in der Vorankündigung fürs Projekt angekreuzt, für
                                            dieses Gewerk noch nicht bestätigt
                                        </div>
                                    ))}
                                </>
                            )}
                            {/* Hinweise der Planungsphase: ein Satz je Nummer, kein Kreuz
                                und kein Knopf — welches Gewerk die Arbeit ausführt,
                                entscheidet der Koordinator (`anhangIIPlanungHinweise`). */}
                            {planungHinweise.map(h => (
                                <div
                                    key={`planung-${h.nummer}`}
                                    style={{ color: HANDBUCH_FARBEN.risikoMittel, fontStyle: 'italic' }}
                                >
                                    ⚠ {h.text}
                                    {h.wortlaute.length > 0 && ` (Katalog: ${h.wortlaute.join(' · ')})`}
                                </div>
                            ))}
                        </div>
                    </Abschnitt>

                    <Abschnitt titel="Gemeinsam genutzte Einrichtungen" className="shrink-0">
                        {meineEinrichtungen.length === 0 ? (
                            <AbschnittLeer text="Im SiGe-Plan ist dieses Gewerk keiner gemeinsam genutzten Einrichtung zugeordnet." />
                        ) : (
                            <AbschnittTabelle kopf={['Einrichtung', 'Mitnutzer', 'Verantwortlich']} breiten={['32%', '43%', '25%']}>
                                {meineEinrichtungen.map((e, i) => (
                                    <AbschnittZeile key={e.bezeichnung} erste={i === 0}>
                                        <AbschnittZelle>{e.bezeichnung}</AbschnittZelle>
                                        <AbschnittZelle>{e.mitnutzer.join(', ')}</AbschnittZelle>
                                        <AbschnittZelle>{e.verantwortlich || '—'}</AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                            </AbschnittTabelle>
                        )}
                    </Abschnitt>
                </div>

                {/* ─── unten rechts: Nachweise, Unterweisungen, Festlegungen ─ */}
                <div className="flex flex-col gap-[6px] min-h-0">
                    <Abschnitt
                        titel="Erforderliche Dokumente und Nachweise"
                        zusatz={`${sortierteNachweise.filter(n => n.status === 'vorhanden').length} von ${sortierteNachweise.length}`}
                        className="flex-1"
                    >
                        {sortierteNachweise.length === 0 ? (
                            <AbschnittLeer text="Für dieses Gewerk ist kein Nachweis gefordert." />
                        ) : (
                            <AbschnittTabelle
                                kopf={['Nr', 'Nachweis', 'Status', 'Frist', 'Beleg', 'Bemerkung']}
                                breiten={['6%', '32%', '14%', '13%', '20%', '15%']}
                            >
                                {sortierteNachweise.map((n, i) => {
                                    const lage = fristLage(n, heuteIso);
                                    const beleg = n.dokument_id ? dokumentNach.get(n.dokument_id) : undefined;
                                    return (
                                        <AbschnittZeile key={n.id} erste={i === 0}>
                                            <AbschnittZelle>{n.nr}</AbschnittZelle>
                                            <AbschnittZelle>{n.bezeichnung}</AbschnittZelle>
                                            <AbschnittZelle>
                                                <NachweisStatusZeichen status={n.status} mitText />
                                            </AbschnittZelle>
                                            <AbschnittZelle>
                                                {formatDatum(n.frist) || '—'}
                                                <div><FristZeichen lage={lage} /></div>
                                            </AbschnittZelle>
                                            <AbschnittZelle>
                                                {beleg ? beleg.dateiname : n.dokument_id ? (
                                                    /* Die Zeile verweist auf ein Dokument, das in der
                                                       geladenen Ablage fehlt — gesperrt, gelöscht oder
                                                       nicht mitgeladen. Das wird BENANNT. */
                                                    <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                                                        Beleg nicht abrufbar
                                                    </span>
                                                ) : n.status === 'vorhanden' ? (
                                                    <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                                                        ohne Beleg in der Ablage
                                                    </span>
                                                ) : '—'}
                                            </AbschnittZelle>
                                            <AbschnittZelle>{n.bemerkung || ''}</AbschnittZelle>
                                        </AbschnittZeile>
                                    );
                                })}
                            </AbschnittTabelle>
                        )}
                    </Abschnitt>

                    <Abschnitt titel="Unterweisungen" className="shrink-0">
                        {(gewerk.unterweisungen || []).length === 0 ? (
                            <AbschnittLeer />
                        ) : (
                            <AbschnittTabelle kopf={['Thema', 'Intervall', 'Nachweis']} breiten={['46%', '20%', '34%']}>
                                {(gewerk.unterweisungen || []).map((u, i) => (
                                    <AbschnittZeile key={`${u.thema}-${i}`} erste={i === 0}>
                                        <AbschnittZelle>{u.thema}</AbschnittZelle>
                                        <AbschnittZelle>{u.intervall}</AbschnittZelle>
                                        <AbschnittZelle>{u.nachweis}</AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                            </AbschnittTabelle>
                        )}
                    </Abschnitt>

                    <Abschnitt titel="Weitere Festlegungen" className="shrink-0">
                        <div style={{ fontSize: '8px', lineHeight: 1.35 }}>
                            {meineZustaendigkeiten.length > 0 && (
                                <div>
                                    <strong>Zuständig:</strong>{' '}
                                    {meineZustaendigkeiten.map(z => `${z.rolle}: ${z.personen.join(', ')}`).join(' · ')}
                                </div>
                            )}
                            <div>
                                <strong>Ersthelfer:</strong>{' '}
                                {gewerk.notfall?.ersthelfer
                                    || (notrufplan?.ersthelfer || []).map(e => e.name).join(', ')
                                    || '—'}
                                {/* Krankenhaus und D-Arzt stehen im Notrufplan des
                                    SiGe-Plans und werden hier nur gelesen. Ein
                                    zweites Eingabefeld ergäbe zwei Krankenhäuser
                                    im selben Projekt. */}
                                {notrufplan?.krankenhaus?.name && (
                                    <> · <strong>Krankenhaus:</strong> {notrufplan.krankenhaus.name}</>
                                )}
                                {notrufplan?.durchgangsarzt?.name && (
                                    <> · <strong>D-Arzt:</strong> {notrufplan.durchgangsarzt.name}</>
                                )}
                            </div>
                            {gewerk.notfall?.besonderheiten && (
                                <div><strong>Besonderheiten:</strong> {gewerk.notfall.besonderheiten}</div>
                            )}
                            {(gewerk.witterung?.eintraege || []).length > 0 && (
                                <div>
                                    <strong>Witterung:</strong>{' '}
                                    {gewerk.witterung.eintraege.map(w => `${w.bedingung} → ${w.massnahme}`).join(' · ')}
                                </div>
                            )}
                            {(gewerk.rab32?.eintraege || []).length > 0 && (
                                <div>
                                    <strong>RAB 32 / spätere Arbeiten:</strong>{' '}
                                    {gewerk.rab32.eintraege
                                        .map(r => r.beschreibung + (r.sicherheitskonzept ? ` — ${r.sicherheitskonzept}` : ''))
                                        .join(' · ')}
                                </div>
                            )}
                            {(gewerk.ordnung?.regeln || []).length > 0 && (
                                <div>
                                    <strong>Gewerkspezifische Regeln:</strong>{' '}
                                    {gewerk.ordnung.regeln.join(' · ')}
                                </div>
                            )}
                            {!notrufplan && !gewerk.notfall?.ersthelfer && (
                                <div style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                                    Kein Notrufplan im SiGe-Plan angelegt — Krankenhaus, D-Arzt und
                                    Ersthelfer fehlen deshalb auf diesem Blatt.
                                </div>
                            )}
                        </div>
                    </Abschnitt>
                </div>
            </div>

            {/* ─── volle Breite: Zeitliche Koordination und Schnittstellen ─── */}
            <div className="shrink-0" style={{ marginTop: '6px' }}>
                <Abschnitt
                    titel="Zeitliche Koordination und Schnittstellengefährdungen"
                    zusatz={achsenStart && achsenEnde
                        ? `${formatDatum(achsenStart)} – ${formatDatum(achsenEnde)}`
                        : 'ohne Zeitachse'}
                >
                    <AbschnittTabelle
                        kopf={['Gewerk', 'Zeitbalken', 'Gefährdung', 'Maßnahme', 'T-O-P']}
                        breiten={['16%', '32%', '24%', '23%', '5%']}
                    >
                        <AbschnittZeile erste>
                            <AbschnittZelle style={{ fontWeight: 700 }}>
                                {gewerk.gewerk_label}
                            </AbschnittZelle>
                            <AbschnittZelle>{balken(zeitraum.von, zeitraum.bis, true)}</AbschnittZelle>
                            <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise }}>
                                eigene Bauzeit
                            </AbschnittZelle>
                            <AbschnittZelle />
                            <AbschnittZelle />
                        </AbschnittZeile>
                        {nachbarn.map(other => {
                            const z = wirksamerZeitraumDerFassung(other, ausPlan, handbuch);
                            const key = paarKey(meinSchluessel, koordinierungsSchluessel(other));
                            const bewertungen = (paareNach.get(key) || []).filter(b => !b.veraltet_seit);
                            return (
                                <AbschnittZeile key={other.id}>
                                    <AbschnittZelle>{other.gewerk_label}</AbschnittZelle>
                                    <AbschnittZelle>{balken(z.von, z.bis, false)}</AbschnittZelle>
                                    <AbschnittZelle>
                                        {bewertungen.length === 0
                                            ? <span style={{ color: HANDBUCH_FARBEN.textLeise }}>—</span>
                                            : bewertungen.map(b => <div key={b.id}>{b.gefaehrdung}</div>)}
                                    </AbschnittZelle>
                                    <AbschnittZelle>
                                        {bewertungen.map(b => <div key={b.id}>{b.massnahme || '—'}</div>)}
                                    </AbschnittZelle>
                                    <AbschnittZelle>
                                        {bewertungen.map(b => <div key={b.id}>{b.top || '—'}</div>)}
                                    </AbschnittZelle>
                                </AbschnittZeile>
                            );
                        })}
                        {nachbarn.length === 0 && (
                            <AbschnittZeile>
                                <AbschnittZelle style={{ color: HANDBUCH_FARBEN.textLeise }}>
                                    Es gibt keine weitere Gewerkeseite — Schnittstellen lassen sich
                                    nicht prüfen.
                                </AbschnittZelle>
                                <AbschnittZelle />
                                <AbschnittZelle />
                                <AbschnittZelle />
                                <AbschnittZelle />
                            </AbschnittZeile>
                        )}
                    </AbschnittTabelle>
                    {(paare || []).length === 0 && nachbarn.length > 0 && (
                        <div style={{ fontSize: '7.5px', color: HANDBUCH_FARBEN.risikoHoch, marginTop: '3px' }}>
                            Zu diesem Projekt liegt noch keine Bewertung aus dem Modul
                            „Gewerke-Überschneidung" vor. Die leere Spalte „Gefährdung" bedeutet
                            deshalb nicht, dass keine besteht.
                        </div>
                    )}
                </Abschnitt>
            </div>
        </A3Seite>
    );
};
